import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Save, Trash2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/souscriptions/admin/envois/modeles`;

const TemplateSelect = ({ modeles, value, onChange, onDelete }) => {
  const current = modeles.find((m) => m.id === value);
  return (
    <div className="flex gap-2">
      <select value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 h-9 rounded-md border border-input bg-white px-3 text-sm" data-testid="email-template-select">
        {modeles.map((m) => <option key={m.id} value={m.id}>{m.builtin ? '' : '★ '}{m.nom}</option>)}
      </select>
      {current && !current.builtin && (
        <Button type="button" variant="outline" size="sm" onClick={() => onDelete(current.id)} className="h-9 text-red-600 border-red-200" data-testid="email-template-delete-btn">
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export const EnvoiEmailDialog = ({ open, onOpenChange, title, description, defaultTemplateId, onSend }) => {
  const { getAuthHeader } = useAuth();
  const [modeles, setModeles] = useState([]);
  const [tplId, setTplId] = useState('');
  const [sujet, setSujet] = useState('');
  const [message, setMessage] = useState('');
  const [newName, setNewName] = useState('');
  const [sending, setSending] = useState(false);

  const applyTemplate = (list, id) => {
    const t = list.find((m) => m.id === id) || list[0];
    if (!t) return;
    setTplId(t.id); setSujet(t.sujet || ''); setMessage(t.message || '');
  };

  const load = async (selectId) => {
    const { data } = await axios.get(API, { headers: getAuthHeader() });
    setModeles(data.modeles);
    applyTemplate(data.modeles, selectId);
  };

  useEffect(() => {
    if (open) load(defaultTemplateId).catch(() => toast.error('Modèles indisponibles'));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTemplate = async () => {
    if (!newName.trim()) { toast.error('Donnez un nom au modèle'); return; }
    try {
      const { data } = await axios.post(API, { nom: newName, sujet, message }, { headers: getAuthHeader() });
      toast.success('Modèle enregistré');
      setNewName('');
      await load(data.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur enregistrement'); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Supprimer ce modèle ?')) return;
    await axios.delete(`${API}/${id}`, { headers: getAuthHeader() });
    toast.success('Modèle supprimé');
    await load(defaultTemplateId);
  };

  const send = async () => {
    if (!message.trim()) { toast.error('Le message est vide'); return; }
    setSending(true);
    try { await onSend({ sujet, message }); onOpenChange(false); } catch { /* toast affiché par l'appelant */ } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="envoi-email-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          <TemplateSelect modeles={modeles} value={tplId} onChange={(id) => applyTemplate(modeles, id)} onDelete={deleteTemplate} />
          <Input value={sujet} onChange={(e) => setSujet(e.target.value)} placeholder="Objet de l'email" data-testid="email-subject-input" />
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={9} data-testid="email-message-input" />
          <p className="text-xs text-gray-500">Variables : {'{raison_sociale}'}, {'{numero_police}'}, {'{siret}'} — remplacées pour chaque membre. Le reçu PDF est joint automatiquement.</p>
          <div className="flex gap-2">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nom du nouveau modèle" className="h-9" data-testid="email-template-name-input" />
            <Button type="button" variant="outline" size="sm" onClick={saveTemplate} className="h-9 gap-1.5 shrink-0" data-testid="email-template-save-btn">
              <Save className="h-4 w-4" /> Enregistrer comme modèle
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="envoi-email-cancel-btn">Annuler</Button>
          <Button onClick={send} disabled={sending} className="bg-indigo-600 hover:bg-indigo-700 gap-1.5" data-testid="envoi-email-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
